import React from 'react';
import { useNavigate } from 'react-router-dom';
import { formatRelativeTime } from './dashboardData';
import { ClipboardDocumentListIcon, WrenchScrewdriverIcon, BanknotesIcon, DocumentTextIcon } from '@heroicons/react/20/solid';

/**
 * ActivityFeed — Last 10 actions across all entities.
 * REQ-DSH-06: Shows entity, action, timestamp. Click navigates to detail.
 */
export default function ActivityFeed({ activities = [], loading = false }) {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={headerStyle}>
          <h3 style={titleStyle}>Recent Activity</h3>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {[1, 2, 3, 4, 5].map(i => (
            <div key={i} style={skeletonRowStyle} />
          ))}
        </div>
      </div>
    );
  }

  const isEmpty = activities.length === 0;

  return (
    <div style={containerStyle}>
      <div style={headerStyle}>
        <h3 style={titleStyle}>Recent Activity</h3>
      </div>

      {isEmpty ? (
        <div style={emptyStyle}>
          <p style={{ margin: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>
            No recent activity
          </p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
          {activities.map((activity, idx) => (
            <div
              key={activity.id || idx}
              onClick={() => activity.link && navigate(activity.link)}
              style={{ ...rowStyle, borderBottom: idx === activities.length - 1 ? 'none' : '1px solid var(--neutral-100)' }}
              onMouseEnter={e => e.currentTarget.style.background = 'var(--neutral-50)'}
              onMouseLeave={e => e.currentTarget.style.background = 'transparent'}
            >
              <span style={iconStyle}>{getTypeIcon(activity.type)}</span>
              <span style={entityStyle}>{activity.entity}</span>
              <span style={actionStyle}>{activity.action}</span>
              <span style={timeStyle}>{formatRelativeTime(activity.date)}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function getTypeIcon(type) {
  const iconProps = { width: 14, height: 14, style: { color: 'var(--color-text-muted)' } };
  switch (type) {
    case 'quote': return <ClipboardDocumentListIcon {...iconProps} />;
    case 'job': return <WrenchScrewdriverIcon {...iconProps} />;
    case 'invoice': return <BanknotesIcon {...iconProps} />;
    default: return <DocumentTextIcon {...iconProps} />;
  }
}

const containerStyle = {
  background: 'var(--color-surface)',
  border: '1px solid var(--neutral-200)',
  borderRadius: "var(--card-radius)",
  padding: 20,
};

const headerStyle = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: 12,
};

const titleStyle = {
  margin: 0,
  fontSize: 16,
  fontWeight: 600,
  color: 'var(--color-text-default)',
};

const emptyStyle = {
  padding: '32px 16px',
  textAlign: 'center',
};

const skeletonRowStyle = {
  height: 32,
  borderRadius: 6,
  background: 'linear-gradient(90deg, var(--neutral-100) 25%, var(--neutral-200) 50%, var(--neutral-100) 75%)',
  backgroundSize: '200% 100%',
  animation: 'shimmer 1.5s infinite',
};

const rowStyle = {
  display: 'flex',
  alignItems: 'center',
  gap: 10,
  padding: '7px 10px',
  borderRadius: 6,
  cursor: 'pointer',
  transition: 'background 0.1s',
};

const iconStyle = {
  fontSize: 14,
  flexShrink: 0,
};

const entityStyle = {
  fontSize: 13,
  fontWeight: 600,
  color: 'var(--color-action-primary)',
  minWidth: 110,
  cursor: 'pointer',
};

const actionStyle = {
  fontSize: 13,
  color: 'var(--color-text-muted)',
  flex: 1,
};

const timeStyle = {
  fontSize: 12,
  color: 'var(--color-text-muted)',
  flexShrink: 0,
};
